// File: frontend/src/pages/timetable/TimetableManagement.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/axios';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CLASSES = ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'];
const PERIOD_TYPES = ['Class', 'Break', 'Lunch', 'Assembly'];

// Standardized Time Presets according to Fix 6
const PERIOD_TIMES = {
  1: { start_time: '08:00 AM', end_time: '08:45 AM' },
  2: { start_time: '08:45 AM', end_time: '09:30 AM' },
  3: { start_time: '09:30 AM', end_time: '10:15 AM' },
  4: { start_time: '10:30 AM', end_time: '11:15 AM' },
  5: { start_time: '11:15 AM', end_time: '12:00 PM' },
  6: { start_time: '12:30 PM', end_time: '01:15 PM' },
  7: { start_time: '01:15 PM', end_time: '02:00 PM' },
  8: { start_time: '02:00 PM', end_time: '02:45 PM' }
};

const DEFAULT_SUBJECTS = [
  { name: 'Mathematics', code: 'MATH-10' },
  { name: 'English', code: 'ENG-10' },
  { name: 'Science', code: 'SCI-10' },
  { name: 'Physics', code: 'PHY-10' },
  { name: 'Chemistry', code: 'CHEM-10' },
  { name: 'Biology', code: 'BIO-10' },
  { name: 'Computer Science', code: 'CS-10' },
  { name: 'Islamic Studies', code: 'ISL-10' },
  { name: 'Urdu', code: 'URDU-10' },
  { name: 'Social Studies', code: 'SST-10' },
  { name: 'History', code: 'HIST-10' }
];

const TimetableManagement = () => {

  // Active View Tab: 'daily' | 'week' | 'teacher'
  const [activeTab, setActiveTab] = useState('daily');
  const [selectedClass, setSelectedClass] = useState('Class 10');
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const [timetable, setTimetable] = useState([]);
  const [teacherTimetable, setTeacherTimetable] = useState([]);
  const [subjectsList, setSubjectsList] = useState(DEFAULT_SUBJECTS);
  const [staffList, setStaffList] = useState([]);

  const { user } = useAuth();
  const { school } = useSchool();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalError, setModalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Copy Day State
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyTargetDay, setCopyTargetDay] = useState('Tuesday');
  const [isCopying, setIsCopying] = useState(false);

  // Add / Edit Entry Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    period_number: 1,
    period_type: 'Class',
    subject: 'Mathematics',
    teacher_id: '',
    teacher_name: '',
    start_time: '08:00 AM',
    end_time: '08:45 AM',
    room_number: '101'
  });

  // Fetch subjects from GET /api/v1/exams/subjects (Fix 1)
  const fetchSubjects = async () => {
    try {
      const res = await api.get('/api/v1/exams/subjects').catch(() => ({ data: null }));
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setSubjectsList(
          res.data.data.map((s) => ({
            name: s.subject_name || s.name || s.subject,
            code: s.subject_code || s.code || 'SUB'
          }))
        );
      }
    } catch (e) {
      console.error('Error fetching subjects:', e);
    }
  };

  // Fetch staff from GET /api/v1/staff (Fix 2)
  const fetchStaff = async () => {
    try {
      const res = await api.get('/api/v1/staff').catch(() => ({ data: null }));
      if (res.data?.success && Array.isArray(res.data.data)) {
        setStaffList(res.data.data);
      }
    } catch (e) {
      console.error('Error fetching staff list:', e);
    }
  };

  // Fetch class timetable entries
  const fetchClassTimetable = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/v1/timetable/class/${encodeURIComponent(selectedClass)}`);
      if (response.data?.success) {
        setTimetable(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching timetable:', err);
      setError(err.response?.data?.message || 'Failed to load timetable entries.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch teacher timetable for Teacher View tab (Fix 7)
  const fetchTeacherTimetable = async (teacherId) => {
    if (!teacherId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/v1/timetable/teacher/${encodeURIComponent(teacherId)}`);
      if (res.data?.success) {
        setTeacherTimetable(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching teacher schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
    fetchStaff();
  }, []);

  useEffect(() => {
    if (activeTab === 'daily' || activeTab === 'week') {
      fetchClassTimetable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, activeTab]);

  useEffect(() => {
    if (activeTab === 'teacher' && selectedTeacherId) {
      fetchTeacherTimetable(selectedTeacherId);
    }
  }, [selectedTeacherId, activeTab]);

  // Handle Period # Change Auto-Fill Times (Fix 6)
  const handlePeriodNumberChange = (pNum, pType = formData.period_type) => {
    let times = { start_time: '08:00 AM', end_time: '08:45 AM' };

    if (pType === 'Break') {
      times = { start_time: '10:15 AM', end_time: '10:30 AM' };
    } else if (pType === 'Lunch') {
      times = { start_time: '12:00 PM', end_time: '12:30 PM' };
    } else if (pType === 'Assembly') {
      times = { start_time: '07:45 AM', end_time: '08:00 AM' };
    } else if (PERIOD_TIMES[pNum]) {
      times = PERIOD_TIMES[pNum];
    }

    setFormData((prev) => ({
      ...prev,
      period_number: pNum,
      period_type: pType,
      ...times
    }));
  };

  const handleOpenAddModal = (pNum = 1) => {
    setEditingEntry(null);
    const times = PERIOD_TIMES[pNum] || { start_time: '08:00 AM', end_time: '08:45 AM' };
    setFormData({
      period_number: pNum,
      period_type: 'Class',
      subject: subjectsList[0]?.name || 'Mathematics',
      teacher_id: staffList[0]?.id || '',
      teacher_name: staffList[0]?.name || '',
      ...times,
      room_number: '101'
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      period_number: entry.period_number,
      period_type: entry.period_type || 'Class',
      subject: entry.subject || '',
      teacher_id: entry.teacher_id || '',
      teacher_name: entry.teacher_name || '',
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      room_number: entry.room_number || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this period slot?')) return;
    try {
      const res = await api.delete(`/api/v1/timetable/${id}`);
      if (res.data?.success) {
        setSuccess('Entry deleted successfully');
        fetchClassTimetable();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setModalError('');
      setError('');
      setSuccess('');

      const payload = {
        class_id: selectedClass,
        day_of_week: selectedDay,
        ...formData
      };

      if (editingEntry) {
        await api.put(`/api/v1/timetable/${editingEntry.id}`, payload);
        setSuccess('Timetable entry updated successfully!');
      } else {
        await api.post('/api/v1/timetable', payload);
        setSuccess('Timetable entry added successfully!');
      }

      setIsModalOpen(false);
      fetchClassTimetable();
    } catch (err) {
      console.error('Submit error:', err);
      const msg = err.response?.data?.message || 'Failed to save timetable entry due to conflict.';
      setModalError(msg);
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy Day Handler (Fix 5)
  const handleCopyDaySubmit = async (e) => {
    e.preventDefault();
    try {
      setIsCopying(true);
      setError('');
      setSuccess('');
      const res = await api.post('/api/v1/timetable/copy', {
        class_id: selectedClass,
        from_day: selectedDay,
        to_day: copyTargetDay
      });

      if (res.data?.success) {
        setSuccess(res.data.message || `Copied all periods from ${selectedDay} to ${copyTargetDay}`);
        setIsCopyModalOpen(false);
        fetchClassTimetable();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy day schedule');
    } finally {
      setIsCopying(false);
    }
  };

  // Subject Color Helper (Fix 4)
  const getSubjectBadgeStyle = (subjName, pType) => {
    if (pType === 'Break' || pType === 'Lunch' || pType === 'Assembly') {
      return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    }
    const name = (subjName || '').toLowerCase();
    if (name.includes('math')) return 'bg-blue-600/20 text-blue-300 border-blue-500/30';
    if (name.includes('eng')) return 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30';
    if (name.includes('sci') || name.includes('phy') || name.includes('chem') || name.includes('bio')) {
      return 'bg-purple-600/20 text-purple-300 border-purple-500/30';
    }
    if (name.includes('isl') || name.includes('quran')) return 'bg-teal-600/20 text-teal-300 border-teal-500/30';
    return 'bg-slate-700/50 text-slate-300 border-slate-600';
  };

  const filteredEntries = timetable.filter((item) => item.day_of_week === selectedDay);

  const isSuperAdmin = user?.role === 'super_admin';
  const isUnlocked = isSuperAdmin || school?.feature_timetable !== 0;

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-8 flex items-center justify-center">
        <div className="bg-slate-800 text-white rounded-3xl p-8 shadow-xl text-center space-y-4 max-w-2xl mx-auto border border-purple-500/30">
          <div className="w-16 h-16 rounded-3xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-3xl font-extrabold mx-auto border border-amber-500/30">
            🔒
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight">Class Timetable - Locked Pro Feature</h2>
          <p className="text-sm text-slate-300">
            The Class Routine & Timetable module is not active for <strong>{school?.name || 'your school portal'}</strong>.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20Class%20Timetable%20Pro%20feature"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-lg transition"
            >
              <span>💬</span>
              <span>Upgrade via WhatsApp (+92 317 1818108)</span>
            </a>

            <a
              href="mailto:waleed.khalid111@gmail.com?subject=Upgrade to Unlock Class Timetable Feature"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition"
            >
              <span>✉️</span>
              <span>Upgrade via Email</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-slate-800/80 p-6 rounded-2xl border border-slate-700/60 shadow-xl backdrop-blur-md">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Class Timetable & Routine</h1>
              <p className="text-xs text-slate-400">Conflict-free period scheduling, weekly matrix views, and staff timetables</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 print:hidden">
            {/* View Mode Toggle Buttons */}
            <div className="flex bg-slate-900/80 p-1 rounded-xl border border-slate-700">
              <button
                onClick={() => setActiveTab('daily')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${activeTab === 'daily' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
              >
                Daily View
              </button>
              <button
                onClick={() => setActiveTab('week')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${activeTab === 'week' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
              >
                Week View 🗓️
              </button>
              <button
                onClick={() => setActiveTab('teacher')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${activeTab === 'teacher' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
              >
                Teacher View 👨‍🏫
              </button>
            </div>

            <button
              onClick={() => window.print()}
              className="px-3.5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium rounded-xl border border-slate-600 transition flex items-center space-x-1.5"
            >
              <span>🖨️ Print</span>
            </button>

            {activeTab !== 'teacher' && (
              <button
                onClick={() => handleOpenAddModal(filteredEntries.length + 1)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-blue-500/20 transition flex items-center space-x-1.5"
              >
                <span>+ Add Period</span>
              </button>
            )}
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-xs flex justify-between items-center font-medium">
            <span>⚠️ {error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 font-bold text-sm">✕</button>
          </div>
        )}

        {success && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-xl text-xs flex justify-between items-center font-medium">
            <span>✅ {success}</span>
            <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-300 font-bold text-sm">✕</button>
          </div>
        )}

        {/* --- TAB 1: DAILY VIEW --- */}
        {activeTab === 'daily' && (
          <div className="space-y-6">
            {/* Controls Bar */}
            <div className="bg-slate-800/60 p-4 rounded-2xl border border-slate-700/60 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <label className="text-xs font-semibold text-slate-300 whitespace-nowrap">Select Class:</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CLASSES.map((cls) => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Day Tabs with Copy Button (Fix 5) */}
              <div className="flex items-center space-x-2 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
                {DAYS.map((day) => (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(day)}
                    className={`px-3.5 py-2 text-xs font-semibold rounded-xl transition whitespace-nowrap ${selectedDay === day
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                        : 'bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                      }`}
                  >
                    {day}
                  </button>
                ))}

                {/* Copy Day Feature Button */}
                <button
                  onClick={() => setIsCopyModalOpen(true)}
                  className="px-3 py-2 bg-indigo-600/30 hover:bg-indigo-600 text-indigo-300 hover:text-white text-xs font-semibold rounded-xl border border-indigo-500/30 transition flex items-center space-x-1"
                  title={`Copy ${selectedDay} periods to another day`}
                >
                  <span>📋 Copy Day...</span>
                </button>
              </div>
            </div>

            {/* Daily Cards Grid (Fix 8) */}
            {loading ? (
              <div className="p-12 text-center text-slate-400">
                <div className="inline-block animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-3"></div>
                <p className="text-xs">Loading schedule details...</p>
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="bg-slate-800/40 p-12 rounded-2xl border border-dashed border-slate-700 text-center">
                <p className="text-slate-300 font-semibold text-sm mb-1">No periods scheduled for {selectedDay} ({selectedClass})</p>
                <p className="text-xs text-slate-500 mb-4">Click below to add a new period slot or copy from another day.</p>
                <button
                  onClick={() => handleOpenAddModal(1)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl transition"
                >
                  + Add First Period
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEntries.map((entry) => {
                  const isBreakOrLunch = entry.period_type === 'Break' || entry.period_type === 'Lunch';
                  return (
                    <div
                      key={entry.id}
                      className={`rounded-2xl border p-5 transition shadow-lg flex flex-col justify-between relative group ${isBreakOrLunch
                          ? 'bg-amber-950/20 border-amber-500/40'
                          : 'bg-slate-800/80 border-slate-700/70 hover:border-blue-500/50'
                        }`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${getSubjectBadgeStyle(entry.subject, entry.period_type)}`}>
                            Period #{entry.period_number} • {entry.period_type || 'Class'}
                          </span>
                          <span className="text-xs font-mono text-slate-300 bg-slate-900/80 px-2.5 py-1 rounded-md border border-slate-700">
                            {entry.start_time} - {entry.end_time}
                          </span>
                        </div>

                        <h3 className="text-lg font-bold text-white mb-2">
                          {entry.subject}
                        </h3>

                        {!isBreakOrLunch && (
                          <div className="space-y-1.5 text-xs text-slate-300">
                            <div className="flex items-center space-x-2">
                              <span className="text-slate-400">Teacher:</span>
                              <strong className="text-slate-100">{entry.teacher_name || 'Unassigned'}</strong>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-slate-400">Room / Venue:</span>
                              <strong className="text-slate-100">{entry.room_number || 'N/A'}</strong>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-end space-x-2 mt-5 pt-3 border-t border-slate-700/50 print:hidden">
                        <button
                          onClick={() => handleOpenEditModal(entry)}
                          className="px-2.5 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="px-2.5 py-1 text-xs bg-red-900/40 hover:bg-red-900/80 text-red-300 rounded-lg transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* --- TAB 2: FULL WEEK MATRIX GRID (Fix 4) --- */}
        {activeTab === 'week' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-800/60 p-4 rounded-2xl border border-slate-700/60">
              <div className="flex items-center space-x-3">
                <span className="text-xs font-semibold text-slate-300">Class:</span>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-1.5 text-xs font-semibold text-white focus:outline-none"
                >
                  {CLASSES.map((cls) => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Subject Color Legend */}
              <div className="hidden lg:flex items-center space-x-3 text-[11px] font-semibold">
                <span className="flex items-center space-x-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span><span>Math</span></span>
                <span className="flex items-center space-x-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span><span>English</span></span>
                <span className="flex items-center space-x-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span><span>Science</span></span>
                <span className="flex items-center space-x-1"><span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span><span>Islamic</span></span>
                <span className="flex items-center space-x-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span><span>Break/Lunch</span></span>
              </div>
            </div>

            {/* Week Matrix Grid Table */}
            <div className="bg-slate-800/80 rounded-2xl border border-slate-700/80 overflow-x-auto shadow-xl">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-900 border-b border-slate-700 text-slate-300 font-bold uppercase tracking-wider">
                    <th className="p-3 border-r border-slate-700 w-28">Period / Time</th>
                    {DAYS.map((day) => (
                      <th key={day} className="p-3 border-r border-slate-700 text-center min-w-[130px]">
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/60">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((pNum) => {
                    const presetTime = PERIOD_TIMES[pNum];
                    return (
                      <tr key={pNum} className="hover:bg-slate-800/50">
                        <td className="p-3 border-r border-slate-700 bg-slate-900/60 font-semibold text-slate-300">
                          <div className="font-bold">Period {pNum}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{presetTime?.start_time}</div>
                        </td>

                        {DAYS.map((day) => {
                          const slot = timetable.find(
                            (item) => item.day_of_week === day && item.period_number === pNum
                          );

                          return (
                            <td key={day} className="p-2 border-r border-slate-700/60 align-top">
                              {slot ? (
                                <div className={`p-2 rounded-xl border text-xs leading-tight ${getSubjectBadgeStyle(slot.subject, slot.period_type)}`}>
                                  <p className="font-bold">{slot.subject}</p>
                                  {slot.teacher_name && (
                                    <p className="text-[10px] opacity-80 mt-0.5">{slot.teacher_name}</p>
                                  )}
                                  {slot.room_number && (
                                    <p className="text-[9px] opacity-60">Rm: {slot.room_number}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="p-2 rounded-xl border border-dashed border-slate-700/40 text-slate-600 text-center text-[10px]">
                                  Free
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- TAB 3: TEACHER TIMETABLE VIEW (Fix 7) --- */}
        {activeTab === 'teacher' && (
          <div className="space-y-4">
            <div className="bg-slate-800/60 p-4 rounded-2xl border border-slate-700/60 flex items-center space-x-3">
              <label className="text-xs font-semibold text-slate-300">Select Teacher:</label>
              <select
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-xs"
              >
                <option value="">-- Choose Staff Teacher --</option>
                {staffList.map((stf) => (
                  <option key={stf.id} value={stf.name || stf.id}>
                    {stf.name} ({stf.designation || 'Teacher'})
                  </option>
                ))}
              </select>
            </div>

            {!selectedTeacherId ? (
              <div className="bg-slate-800/40 p-12 rounded-2xl border border-dashed border-slate-700 text-center text-slate-400 text-xs">
                Select a teacher above to view their weekly teaching schedule.
              </div>
            ) : (
              <div className="bg-slate-800/80 rounded-2xl border border-slate-700/80 overflow-x-auto shadow-xl">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-900 border-b border-slate-700 text-slate-300 font-bold uppercase tracking-wider">
                      <th className="p-3 border-r border-slate-700 w-28">Period</th>
                      {DAYS.map((day) => (
                        <th key={day} className="p-3 border-r border-slate-700 text-center min-w-[130px]">
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/60">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((pNum) => (
                      <tr key={pNum} className="hover:bg-slate-800/50">
                        <td className="p-3 border-r border-slate-700 bg-slate-900/60 font-semibold text-slate-300">
                          Period {pNum}
                        </td>
                        {DAYS.map((day) => {
                          const slot = teacherTimetable.find(
                            (item) => item.day_of_week === day && item.period_number === pNum
                          );
                          return (
                            <td key={day} className="p-2 border-r border-slate-700/60 align-top">
                              {slot ? (
                                <div className="p-2.5 bg-purple-600/20 border border-purple-500/40 rounded-xl text-purple-200">
                                  <p className="font-bold text-xs">{slot.class_id} ({slot.section || 'A'})</p>
                                  <p className="text-[11px] font-semibold text-white mt-0.5">{slot.subject}</p>
                                  <p className="text-[10px] text-slate-400 font-mono mt-1">{slot.start_time} - {slot.end_time}</p>
                                </div>
                              ) : (
                                <div className="p-2 rounded-xl border border-dashed border-slate-700/40 text-slate-600 text-center text-[10px]">
                                  Free
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* --- ADD / EDIT PERIOD MODAL FORM --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-700 pb-3">
              <h2 className="text-lg font-bold text-white">
                {editingEntry ? 'Edit Period Slot' : 'Add New Period Slot'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white font-bold">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {modalError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-xs flex justify-between items-center font-medium">
                  <span>⚠️ {modalError}</span>
                  <button type="button" onClick={() => setModalError('')} className="font-bold text-sm text-red-400">✕</button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Class</label>
                  <input type="text" disabled value={selectedClass} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-400 font-semibold" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Day</label>
                  <input type="text" disabled value={selectedDay} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-400 font-semibold" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Period # Selector */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Period #</label>
                  <select
                    value={formData.period_number}
                    onChange={(e) => handlePeriodNumberChange(parseInt(e.target.value))}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>Period {num}</option>
                    ))}
                  </select>
                </div>

                {/* Period Type Selector (Fix 3) */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Period Type</label>
                  <select
                    value={formData.period_type}
                    onChange={(e) => handlePeriodNumberChange(formData.period_number, e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PERIOD_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Subject Dropdown (Fix 1) */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Subject</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {subjectsList.map((sb, idx) => (
                      <option key={idx} value={sb.name}>
                        {sb.name} ({sb.code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Teacher Dropdown (Fix 2) */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Teacher</label>
                  <select
                    value={formData.teacher_name}
                    onChange={(e) => {
                      const selectedStaff = staffList.find((s) => s.name === e.target.value);
                      setFormData({
                        ...formData,
                        teacher_name: e.target.value,
                        teacher_id: selectedStaff ? selectedStaff.id : ''
                      });
                    }}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Unassigned --</option>
                    {staffList.map((stf) => (
                      <option key={stf.id} value={stf.name}>
                        {stf.name} ({stf.designation || 'Teacher'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Start & End Times (Auto-filled via Fix 6) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Start Time</label>
                  <input
                    type="text"
                    required
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">End Time</label>
                  <input
                    type="text"
                    required
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Room / Lab Number</label>
                <input
                  type="text"
                  placeholder="e.g. Room 102"
                  value={formData.room_number}
                  onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl shadow-lg transition disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : editingEntry ? 'Save Changes' : 'Create Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- COPY DAY MODAL (Fix 5) --- */}
      {isCopyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-700 pb-3">
              <h3 className="text-base font-bold text-white">Copy Day Schedule</h3>
              <button onClick={() => setIsCopyModalOpen(false)} className="text-slate-400 hover:text-white font-bold">✕</button>
            </div>

            <form onSubmit={handleCopyDaySubmit} className="space-y-4">
              <p className="text-xs text-slate-300">
                Copy all period slots from <strong className="text-blue-400">{selectedDay}</strong> ({selectedClass}) to another day:
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Target Day (Copy to):</label>
                <select
                  value={copyTargetDay}
                  onChange={(e) => setCopyTargetDay(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DAYS.filter((d) => d !== selectedDay).map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => setIsCopyModalOpen(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCopying}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl shadow-lg transition"
                >
                  {isCopying ? 'Copying...' : 'Copy Timetable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetableManagement;
